import {
  ChangeDetectionStrategy,
  Component,
  OnInit,
  inject,
} from '@angular/core';
import { ActivatedRoute } from '@angular/router';
import { Store } from '@ngxs/store';
import { FilterService } from '@wilson/filter';
import { ZOOM_LEVEL, ZoomLevelQueryParamKey } from '@wilson/interfaces';
import {
  DragAndDropModeService,
  SELECTED_REGION,
  TabEnum,
} from '@wilson/shift-timeline/services';
import {
  ChangeNumberOfDaysInDatepicker,
  ClearDeterminedShiftActivities,
  ClearShiftActivitiesRows,
  SetAreActivitiesVisible,
  SetAreStaysVisible,
  SetSelectedUnassignedRegion,
  SetZoomLevel,
  ShiftTimelineSettingsState,
} from '@wilson/shift-timeline/state';
import { Observable } from 'rxjs';

interface HourRange {
  value: ZOOM_LEVEL;
  name: string;
}

const AllHoursRange: HourRange[] = [
  {
    value: ZOOM_LEVEL.HALF_WEEK,
    name: 'page.shift_timeline.view_mode.3_days',
  },
  {
    value: ZOOM_LEVEL.DEFAULT,
    name: 'page.shift_timeline.view_mode.7_days',
  },
  {
    value: ZOOM_LEVEL.TEN_DAYS,
    name: 'page.shift_timeline.view_mode.10_days',
  },
  {
    value: ZOOM_LEVEL.HALF_MONTH,
    name: 'page.shift_timeline.view_mode.14_days',
  },
  {
    value: ZOOM_LEVEL.MONTH,
    name: 'page.shift_timeline.view_mode.31_days',
  },
];

@Component({
  selector: 'wilson-timeline-zoom-level',
  templateUrl: './timeline-zoom-level.component.html',
  styleUrls: ['./timeline-zoom-level.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: false,
})
export class TimelineZoomLevelComponent implements OnInit {
  private readonly store = inject(Store);
  private readonly filterService = inject(FilterService);
  private readonly route = inject(ActivatedRoute);
  private readonly dragAndDropModeService = inject(DragAndDropModeService);

  selectedZoomLevel$!: Observable<ZOOM_LEVEL>;

  hoursRange: HourRange[] = AllHoursRange;

  ngOnInit(): void {
    const timelineZoomLevel: string =
      this.route.snapshot.queryParams[ZoomLevelQueryParamKey];
    const zoomLevel = this.store.selectSnapshot(
      ShiftTimelineSettingsState.zoomLevel,
    );
    if (
      timelineZoomLevel &&
      Object.values(ZOOM_LEVEL).includes(timelineZoomLevel as ZOOM_LEVEL)
    ) {
      this.onSelectedZoomLevel(timelineZoomLevel as ZOOM_LEVEL);
    } else if (zoomLevel === ZOOM_LEVEL.DAY) {
      this.onSelectedZoomLevel(ZOOM_LEVEL.DEFAULT);
    }

    this.selectedZoomLevel$ = this.store.select(
      ShiftTimelineSettingsState.zoomLevel,
    );
  }

  async onSelectedZoomLevel(zoomLevel: ZOOM_LEVEL): Promise<void> {
    setTimeout(() => {
      if (zoomLevel == ZOOM_LEVEL.HALF_MONTH) {
        this.store.dispatch(new ChangeNumberOfDaysInDatepicker(29));
      } else {
        this.store.dispatch(new ChangeNumberOfDaysInDatepicker(6));
      }
    }, 0);

    setTimeout(() => {
      this.filterService.saveValueInQueryParam(
        ZoomLevelQueryParamKey,
        zoomLevel.toString(),
      );
    }, 0);
    this.store.dispatch([new SetZoomLevel(zoomLevel)]);

    if (zoomLevel == ZOOM_LEVEL.MONTH) {
      this.filterService.saveValueInQueryParam(
        SELECTED_REGION,
        TabEnum.UnassignedShifts.toString(),
      );

      this.store.dispatch([
        new SetSelectedUnassignedRegion(TabEnum.UnassignedShifts),
        new SetAreStaysVisible(false),
        new SetAreActivitiesVisible(false),
      ]);
    }
    this.store.dispatch([
      new ClearDeterminedShiftActivities(),
      new ClearShiftActivitiesRows(),
    ]);

    this.dragAndDropModeService.setSnappingStepSize(zoomLevel);
  }
}
